import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ROLE_LABEL, useAdmins } from '@/features/admins/api'
import {
  ACTION_LABEL,
  actionTone,
  AUDIT_ACTIONS,
  useAuditLog,
  type AuditChange,
  type AuditRow,
} from '@/features/audit/api'
import { localDate, localStamp } from '@/lib/datetime'

const PAGE_SIZE = 20
const ANY_ACTION = 'all'
const ANY_ACTOR = 'all'

export function AuditPage() {
  const [page, setPage] = useState(1)
  const [actor, setActor] = useState(ANY_ACTOR)
  const [action, setAction] = useState(ANY_ACTION)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [inspecting, setInspecting] = useState<AuditRow | null>(null)

  /**
   * The picker is filled from `GET /admins`, which both this screen and Admins &
   * Roles are Super-Admin-gated behind, so it costs nothing extra and shares one
   * cache entry between them.
   *
   * It lists **current role holders**, which is narrower than what the log can
   * contain: rows outlive both role revocation and account deletion by design, so
   * a former admin's actions are still in the log with no way to select them. The
   * complete list is a `SELECT DISTINCT actor_public_id` over `staff_audit_logs`
   * and wants its own endpoint — spec Q26. Filtering by a current admin is the
   * case that actually comes up, so it ships now rather than waiting.
   */
  const actors = useAdmins()

  const query = useAuditLog({
    page,
    pageSize: PAGE_SIZE,
    actorId: actor === ANY_ACTOR ? undefined : actor,
    action: action === ANY_ACTION ? undefined : action,
    from: from || undefined,
    to: to || undefined,
  })

  const columns = useMemo<ColumnDef<AuditRow, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'When',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {localDate(row.original.created_at)}{' '}
            <span className="text-faint">{localStamp(row.original.created_at).slice(11)}</span>
          </span>
        ),
      },
      {
        accessorKey: 'actor_name',
        header: 'Actor',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.actor_name}</div>
            <div className="text-xs text-faint">
              {row.original.actor_role === 'super_admin' ? 'Super' : 'Ops'}
              {row.original.api_token && (
                <> · via API token {row.original.api_token.name}</>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <Badge variant={actionTone(row.original.action)}>
            {ACTION_LABEL[row.original.action] ?? row.original.action}
          </Badge>
        ),
      },
      {
        accessorKey: 'target_desc',
        header: 'Target',
        cell: ({ row }) => <span>{row.original.target_desc}</span>,
      },
      {
        id: 'details',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setInspecting(row.original)}>
              {row.original.changes ? 'View changes' : 'Details'}
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader title="Audit Log" />

      <Notice>
        <b>Super Admin only.</b> An immutable record of every mutating action — reward grants and
        rejections, quest edits, banner and trending changes, and every role change. Approval is{' '}
        <b>single-step</b> in Phase 1, so this trail is the accountability control in place of
        maker–checker. It is append-only: nothing here can be edited or deleted.
      </Notice>

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="audit-actor">Actor</Label>
            <Select
              value={actor}
              onValueChange={(value) => {
                setActor(value)
                setPage(1)
              }}
            >
              <SelectTrigger id="audit-actor" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_ACTOR}>All actors</SelectItem>
                {(actors.data ?? []).map((admin) => (
                  <SelectItem key={admin.id} value={admin.id}>
                    {admin.display_name} · {ROLE_LABEL[admin.role].replace(' Admin', '')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="audit-action">Action</Label>
            <Select
              value={action}
              onValueChange={(value) => {
                setAction(value)
                setPage(1)
              }}
            >
              <SelectTrigger id="audit-action" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_ACTION}>All actions</SelectItem>
                {AUDIT_ACTIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ACTION_LABEL[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="audit-from">From</Label>
            <Input
              id="audit-from"
              type="date"
              value={from}
              className="w-40"
              onChange={(event) => {
                setFrom(event.target.value)
                setPage(1)
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="audit-to">To</Label>
            <Input
              id="audit-to"
              type="date"
              value={to}
              className="w-40"
              onChange={(event) => {
                setTo(event.target.value)
                setPage(1)
              }}
            />
          </div>

          {(actor !== ANY_ACTOR || action !== ANY_ACTION || from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActor(ANY_ACTOR)
                setAction(ANY_ACTION)
                setFrom('')
                setTo('')
                setPage(1)
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          query={query}
          pagination={{ page, onPageChange: setPage }}
          emptyMessage="No entries match these filters."
        />
      </Card>

      <Dialog open={inspecting !== null} onOpenChange={(open) => !open && setInspecting(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {inspecting ? (ACTION_LABEL[inspecting.action] ?? inspecting.action) : ''}
            </DialogTitle>
            <DialogDescription>{inspecting?.target_desc}</DialogDescription>
          </DialogHeader>

          {inspecting && (
            <div className="space-y-4 text-sm">
              <dl className="space-y-1.5">
                <Row label="When">{localStamp(inspecting.created_at)}</Row>
                <Row label="Actor">
                  {inspecting.actor_name} · {inspecting.actor_email}
                </Row>
                {inspecting.api_token && (
                  <Row label="Via API token">
                    {inspecting.api_token.name} ·{' '}
                    <code className="font-mono text-xs">{inspecting.api_token.token_prefix}…</code>{' '}
                    — made by a script acting as the actor, not by the actor by hand
                  </Row>
                )}
                <Row label="Role at the time">
                  {inspecting.actor_role === 'super_admin' ? 'Super Admin' : 'Operations Admin'}
                </Row>
                <Row label="Target">
                  {inspecting.target_type} · {inspecting.target_id}
                </Row>
                {inspecting.ip_address && <Row label="IP">{inspecting.ip_address}</Row>}
              </dl>

              {inspecting.changes ? (
                <div>
                  <p className="mb-2 font-semibold">What changed</p>
                  <ChangeList changes={inspecting.changes} />
                </div>
              ) : (
                <p className="text-xs text-faint">
                  No field diff — this action created or decided something rather than editing it.
                </p>
              )}

              <p className="text-xs text-faint">
                Actor and target are stored as text, so this entry stays readable even if the
                account is later deleted.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-36 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  )
}

/** Turns "Reuben updated a quest" into "ends_at: 2026-08-31 → 2026-09-15". */
function ChangeList({ changes }: { changes: Record<string, AuditChange> }) {
  return (
    <ul className="space-y-2">
      {Object.entries(changes).map(([field, { from, to }]) => (
        <li key={field} className="rounded-md bg-secondary px-3 py-2">
          <div className="text-xs font-semibold text-muted-foreground">{field}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="rounded bg-destructive-soft px-1.5 py-0.5 text-xs text-destructive line-through">
              {format(from)}
            </span>
            <span className="text-faint">→</span>
            <span className="rounded bg-success-soft px-1.5 py-0.5 text-xs text-[#0f8f5f]">
              {format(to)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  )
}

function format(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}
